<template>
  <Transition name="modal">
    <div v-if="isOpen && testimonial" class="tm-backdrop" @click.self="$emit('close')">
      <div
        class="tm-content"
        role="dialog"
        aria-modal="true"
        :aria-label="`${testimonial.name} testimonial`"
      >
        <div class="tm-header">
          <div class="tm-identity">
            <div class="tm-avatar">{{ testimonial.initials || testimonial.name?.[0] }}</div>
            <div class="tm-title-block">
              <h3 class="tm-name">{{ testimonial.name }}</h3>
              <div class="tm-meta">
                <span class="tm-role">{{ testimonial.role }}</span>
                <span class="tm-sep">·</span>
                <span class="tm-company">{{ testimonial.company }}</span>
              </div>
            </div>
          </div>
          <button class="tm-close" @click="$emit('close')" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <div class="tm-body">
          <blockquote class="tm-quote">"{{ testimonial.quote }}"</blockquote>
        </div>

        <div class="tm-footer">
          <a
            v-if="testimonial.linkedin"
            :href="testimonial.linkedin"
            target="_blank"
            rel="noopener noreferrer"
            class="tm-linkedin-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
              />
            </svg>
            View on LinkedIn
          </a>
          <button class="tm-close-btn" @click="$emit('close')">
            {{ $t('common.close') || 'Close' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { watch, onUnmounted } from 'vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  testimonial: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close'])

const lockScroll = () => {
  document.body.style.overflow = 'hidden'
  document.documentElement.style.overflow = 'hidden'
  document.documentElement.classList.add('lenis-stopped')
  window.lenis?.stop()
}

const unlockScroll = () => {
  document.body.style.overflow = ''
  document.documentElement.style.overflow = ''
  document.documentElement.classList.remove('lenis-stopped')
  window.lenis?.start()
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      lockScroll()
    } else {
      unlockScroll()
    }
  }
)

onUnmounted(() => {
  unlockScroll()
})

const handleKeydown = (e) => {
  if (e.key === 'Escape' && props.isOpen) {
    emit('close')
  }
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      document.addEventListener('keydown', handleKeydown)
    } else {
      document.removeEventListener('keydown', handleKeydown)
    }
  }
)

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Backdrop */
.tm-backdrop {
  @apply fixed inset-0 flex items-center justify-center;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(8px);
}

/* Content */
.tm-content {
  @apply w-full mx-4 overflow-hidden;
  max-width: 520px;
  max-height: 85vh;
  border-radius: 1.5rem;
  background: var(--color-deep-black);
  border: 1px solid var(--color-border);
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(139, 92, 246, 0.1);
  display: flex;
  flex-direction: column;
}

/* Header */
.tm-header {
  @apply flex items-center justify-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.tm-identity {
  @apply flex items-center gap-3.5;
  min-width: 0;
}

.tm-avatar {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--color-electric-purple);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.tm-title-block {
  @apply flex flex-col gap-0.5;
  min-width: 0;
}

.tm-name {
  @apply text-base font-bold truncate;
  color: var(--color-text-primary);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.tm-meta {
  @apply flex items-center gap-1.5 text-xs;
  color: var(--color-text-muted);
  min-width: 0;
  overflow: hidden;
}

.tm-role {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tm-sep {
  flex-shrink: 0;
  opacity: 0.4;
  font-size: 0.6rem;
}

.tm-company {
  flex-shrink: 0;
}

.tm-close {
  @apply flex items-center justify-center flex-shrink-0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  background: transparent;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.15s ease;
}

.tm-close:hover {
  border-color: var(--color-electric-purple);
  color: var(--color-text-primary);
  background: rgba(139, 92, 246, 0.1);
  transform: scale(1.05);
}

.tm-close:active {
  transform: scale(0.95);
}

/* Body */
.tm-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1 1 auto;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) transparent;
}

.tm-body::-webkit-scrollbar {
  width: 4px;
}

.tm-body::-webkit-scrollbar-track {
  background: transparent;
}

.tm-body::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 2px;
}

.tm-quote {
  @apply text-sm leading-relaxed;
  color: var(--color-text-secondary);
  quotes: none;
  font-style: italic;
}

/* Footer */
.tm-footer {
  @apply flex items-center justify-between gap-3;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.tm-linkedin-btn {
  @apply flex items-center gap-1.5 text-xs font-semibold;
  padding: 0.45rem 0.9rem;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  background: transparent;
  text-decoration: none;
  transition:
    border-color 0.2s ease,
    color 0.2s ease,
    background-color 0.2s ease;
  flex-shrink: 0;
}

.tm-linkedin-btn:hover {
  border-color: #0077b5;
  color: #0077b5;
  background: rgba(0, 119, 181, 0.08);
}

.tm-close-btn {
  @apply text-xs font-semibold;
  padding: 0.45rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  background: transparent;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    color 0.2s ease;
}

.tm-close-btn:hover {
  border-color: var(--color-electric-purple);
  color: var(--color-text-primary);
}

/* Transitions */
.modal-enter-active {
  transition: opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.modal-leave-active {
  transition: opacity 0.2s ease-in;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .tm-content {
  animation: tm-in 0.45s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both;
}

.modal-leave-active .tm-content {
  animation: tm-out 0.18s cubic-bezier(0.4, 0, 1, 1) forwards;
}

@keyframes tm-in {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(24px);
    filter: blur(4px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
    filter: blur(0);
  }
}

@keyframes tm-out {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
    filter: blur(0);
  }
  to {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
    filter: blur(3px);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .tm-content {
    max-width: none;
    margin: 0 1rem;
    max-height: 90vh;
    border-radius: 1.25rem;
  }

  .tm-header {
    padding: 1.25rem 1.25rem 0.75rem;
  }

  .tm-body {
    padding: 1.25rem;
  }

  .tm-footer {
    padding: 0.75rem 1.25rem 1.25rem;
  }
}
</style>
